import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useVerbandbuch from '../../hooks/useVerbandbuch';

const READ_RIGHT = 'verbandbuch:read';
const READ_OWN_RIGHT = 'verbandbuch:read:own';
const DELETE_RIGHT = 'verbandbuch:delete';

const defaultFrom = () => dayjs().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD');
const defaultTo = () => dayjs().format('YYYY-MM-DD');

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—');

// Detail rows for the entry dialog: [label, value accessor].
const detailRows = entry => [
  ['Standort', entry.branchName || '—'],
  ['Name des Verletzten / Erkrankten', entry.injuredName],
  ['Erreichbar unter', entry.injuredContact],
  ['Arbeitsbereich / Tätigkeit', entry.workArea],
  ['Datum und Uhrzeit des Unfalls', formatDateTime(entry.accidentAt)],
  ['Unfallort', entry.accidentLocation || '—'],
  ['Hergang des Unfalls', entry.accidentDescription],
  ['Art und Umfang der Verletzung / Erkrankung', entry.injuryDescription],
  ['Zeugen', entry.witnesses || '—'],
  ['Erste-Hilfe-Leistung am', formatDateTime(entry.firstAidAt)],
  ['Erste-Hilfe-Maßnahmen', entry.firstAidMeasures || '—'],
  ['Ersthelfer/in', entry.firstAiderName || '—'],
  ['Erfasst von', entry.createdByName || '—'],
  ['Erfasst am', formatDateTime(entry.createdAt)],
];

const VerbandbuchStatistics = () => {
  const authCtx = useContext(AuthContext);
  const canFull = authCtx.checkRight(READ_RIGHT);
  const canOwn = authCtx.checkRight(READ_OWN_RIGHT);
  const canDelete = authCtx.checkRight(DELETE_RIGHT);
  const canView = canFull || canOwn;
  const { getStatistics, downloadExport, deleteEntry } = useVerbandbuch();

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [branchId, setBranchId] = useState('');
  const [appliedFilter, setAppliedFilter] = useState(() => ({
    from: defaultFrom(),
    to: defaultTo(),
    branchId: '',
  }));
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getStatistics(appliedFilter);
      setStats(result);
    } catch (error) {
      toast.error('Fehler beim Laden der Verbandbuch-Auswertung.');
    } finally {
      setIsLoading(false);
    }
  }, [getStatistics, appliedFilter]);

  useEffect(() => {
    if (!canView) return;
    loadStats();
  }, [canView, loadStats]);

  const handleApply = useCallback(() => {
    const from = fromRef.current?.value || defaultFrom();
    const to = toRef.current?.value || defaultTo();
    if (dayjs(from).isAfter(dayjs(to))) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return;
    }
    setAppliedFilter({ from, to, branchId });
  }, [branchId]);

  const handleDownload = useCallback(
    async format => {
      setDownloading(format);
      try {
        const blob = await downloadExport(format, appliedFilter);
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `verbandbuch-${appliedFilter.from}_${appliedFilter.to}.${
          format === 'xlsx' ? 'xlsx' : 'csv'
        }`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error('Fehler beim Herunterladen der Datei.');
      } finally {
        setDownloading(null);
      }
    },
    [downloadExport, appliedFilter]
  );

  const closeDialog = useCallback(() => {
    setSelectedEntry(null);
    setConfirmDelete(false);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selectedEntry) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setIsDeleting(true);
    try {
      await deleteEntry(selectedEntry.id);
      toast.success('Verbandbuch-Eintrag gelöscht.');
      closeDialog();
      await loadStats();
    } catch (error) {
      toast.error('Fehler beim Löschen des Verbandbuch-Eintrags.');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedEntry, confirmDelete, deleteEntry, closeDialog, loadStats]);

  const chartData = useMemo(
    () =>
      (stats?.byMonth || []).map(month => ({
        name: dayjs(`${month.month}-01`).format('MM.YY'),
        Einträge: month.count,
      })),
    [stats]
  );

  if (!canView) {
    return <Navigate to='/welcome' replace />;
  }

  const branchOptions = stats?.branches || [];
  const monthCount = (stats?.byMonth || []).length;
  const kpiCards = stats
    ? [
        { label: 'Einträge gesamt', value: stats.total },
        { label: 'Betroffene Standorte', value: (stats.byBranch || []).length },
        {
          label: 'Ø pro Monat',
          value: monthCount > 0 ? (stats.total / monthCount).toFixed(1) : '0',
        },
        { label: 'Häufigster Bereich', value: stats.byWorkArea?.[0]?.label || '—' },
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant='h5' component='h1' sx={{ mb: 2 }}>
        Verbandbuch-Auswertung
      </Typography>

      {/* Filter bar */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <TextField
            label='Von'
            type='date'
            inputRef={fromRef}
            defaultValue={appliedFilter.from}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <TextField
            label='Bis'
            type='date'
            inputRef={toRef}
            defaultValue={appliedFilter.to}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <FormControl size='small' sx={{ minWidth: 220 }}>
            <InputLabel id='verbandbuch-stats-branch'>Standort</InputLabel>
            <Select
              labelId='verbandbuch-stats-branch'
              label='Standort'
              value={branchId}
              onChange={event => setBranchId(event.target.value)}
            >
              <MenuItem value=''>
                {stats?.scope === 'own' ? 'Alle eigenen Standorte' : 'Alle Standorte'}
              </MenuItem>
              {branchOptions.map(branch => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant='contained' onClick={handleApply}>
            Anwenden
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              startIcon={
                downloading === 'csv' ? <CircularProgress size={16} /> : <DescriptionIcon />
              }
              disabled={downloading !== null}
              onClick={() => handleDownload('csv')}
            >
              CSV
            </Button>
            <Button
              variant='outlined'
              startIcon={
                downloading === 'xlsx' ? <CircularProgress size={16} /> : <TableChartIcon />
              }
              disabled={downloading !== null}
              onClick={() => handleDownload('xlsx')}
            >
              Excel
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isLoading && !stats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpiCards.map(card => (
              <Grid item xs={6} sm={4} md={3} key={card.label}>
                <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                  <Typography variant='caption' color='text.secondary'>
                    {card.label}
                  </Typography>
                  <Typography variant='h5' sx={{ wordBreak: 'break-word' }}>
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Entries per month chart */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Einträge pro Monat
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey='Einträge' fill='#1976d2' />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Per branch table */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Je Standort
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Standort</TableCell>
                    <TableCell align='right'>Einträge</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.byBranch || []).map(branch => (
                    <TableRow key={branch.branchId}>
                      <TableCell>{branch.branchName || '—'}</TableCell>
                      <TableCell align='right'>{branch.count}</TableCell>
                    </TableRow>
                  ))}
                  {(stats?.byBranch || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align='center'>
                        Keine Daten im gewählten Zeitraum.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Entry list */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Einträge
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Unfallzeitpunkt</TableCell>
                    <TableCell>Standort</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Arbeitsbereich</TableCell>
                    <TableCell>Verletzung / Erkrankung</TableCell>
                    <TableCell>Ersthelfer/in</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.entries || []).map(entry => (
                    <TableRow
                      key={entry.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedEntry(entry);
                        setConfirmDelete(false);
                      }}
                    >
                      <TableCell>{formatDateTime(entry.accidentAt)}</TableCell>
                      <TableCell>{entry.branchName || '—'}</TableCell>
                      <TableCell>{entry.injuredName}</TableCell>
                      <TableCell>{entry.workArea}</TableCell>
                      <TableCell>{entry.injuryDescription}</TableCell>
                      <TableCell>{entry.firstAiderName || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {(stats?.entries || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align='center'>
                        Keine Einträge im gewählten Zeitraum.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Entry details dialog */}
      <Dialog open={!!selectedEntry} onClose={closeDialog} maxWidth='sm' fullWidth>
        <DialogTitle>Verbandbuch-Eintrag</DialogTitle>
        <DialogContent dividers>
          {selectedEntry &&
            detailRows(selectedEntry).map(([label, value]) => (
              <Box key={label} sx={{ mb: 1.5 }}>
                <Typography variant='caption' color='text.secondary'>
                  {label}
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                  {value}
                </Typography>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          {canDelete && (
            <Button
              color='error'
              onClick={handleDelete}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} color='inherit' /> : null}
            >
              {confirmDelete ? 'Endgültig löschen' : 'Löschen'}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={closeDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerbandbuchStatistics;
